import type { Translation } from "../types";

const en: Translation = {
    // Navigation
    "nav.home": "Home",
    "nav.tools": "Tools",
    "nav.minddeck": "MindDeck",
    "nav.jsonparser": "JSON Parser",
    "nav.guide": "Guide",
    "nav.about": "About",
    "nav.contact": "Contact",
    "nav.faq": "FAQ",
    "nav.privacy": "Privacy",
    "nav.terms": "Terms",
    "nav.changelog": "Changelog",
    "nav.roadmap": "Roadmap",

    // Common
    "common.loading": "Loading...",
    "common.error": "An error occurred",
    "common.back": "Back",
    "common.next": "Next",
    "common.previous": "Previous",
    "common.search": "Search",
    "common.menu": "Menu",
    "common.close": "Close",
    "common.learnMore": "Learn More",

    // Home
    "home.title": "Tools for Developers & Learners",
    "home.subtitle":
        "Boost your productivity with efficient and intuitive web-based tools",
    "home.tools.title": "Available Tools",
    "home.minddeck.title": "MindDeck",
    "home.minddeck.desc": "Smart flashcard learning tool",
    "home.jsonparser.title": "JSON Diff Parser",
    "home.jsonparser.desc": "JSON structure comparison and analysis tool",
    "home.features.title": "Key Features",
    "home.features.performance.title": "Fast Performance",
    "home.features.performance.desc":
        "Fast and efficient processing with optimized web technologies",
    "home.features.security.title": "Secure Usage",
    "home.features.security.desc":
        "Client-side processing ensures data security",
    "home.features.multilingual.title": "Multilingual Support",
    "home.features.multilingual.desc":
        "Support for Korean, English, Japanese, and Chinese",
    "home.cta.title": "Get Started Now",
    "home.cta.subtitle": "Take advantage of all the tools provided for free",
    "home.cta.minddeck": "Start MindDeck",
    "home.cta.jsonparser": "Use JSON Parser",
    "home.learnMore": "Learn More",

    // About
    "about.title": "About MewSeeds",
    "about.subtitle":
        "We are a team creating innovative web tools for developers and learners",
    "about.stats.tools": "Available Tools",
    "about.stats.languages": "Supported Languages",
    "about.stats.users": "Users",
    "about.stats.uptime": "Uptime",
    "about.mission.title": "Our Mission",
    "about.mission.description":
        "We simplify complex tasks and provide tools that help developers and learners work more efficiently. We prioritize user experience and continuously innovate to create better solutions.",
    "about.values.title": "Core Values",
    "about.values.innovation.title": "Innovation",
    "about.values.innovation.desc":
        "We leverage the latest technologies to provide the best user experience.",
    "about.values.userFirst.title": "User-First",
    "about.values.userFirst.desc":
        "We develop products based on user needs and feedback.",
    "about.values.privacy.title": "Privacy",
    "about.values.privacy.desc":
        "We protect user privacy and provide secure services.",
    "about.values.openSource.title": "Open Source",
    "about.values.openSource.desc":
        "We create better tools through transparency and collaboration.",
    "about.techStack.title": "Tech Stack",
    "about.contact.title": "Get in Touch",
    "about.contact.description":
        "Feel free to contact us for any questions or suggestions.",
    "about.contact.email": "Send Email",
    "about.contact.github": "GitHub",

    // Footer
    "footer.copyright": "© 2025 MewSeeds. All rights reserved.",
    "footer.madeWith": "MewSeeds, for a better world",
    "footer.links": "Quick Links",
    "footer.tools": "Tools",
    "footer.support": "Support",
    "footer.contact": "Contact",
    "footer.goto.minddeck": "Go to MindDeck",
    "footer.goto.jsonparser": "Go to JSON Parser",

    // Guide
    "guide.title": "User Guide",
    "guide.subtitle":
        "Step-by-step instructions for making the most of all MewSeeds tools.",
    "guide.minddeck.title": "MindDeck User Guide",
    "guide.minddeck.desc":
        "Learn all the features of the flashcard-based learning tool MindDeck step by step.",
    "guide.minddeck.step1": "Create an account and log in",
    "guide.minddeck.step2": "Create a new deck",
    "guide.minddeck.step3": "Add and edit cards",
    "guide.minddeck.step4": "Start study mode",
    "guide.minddeck.step5": "Track progress and view statistics",
    "guide.jsonparser.title": "How to Use JSON Parser",
    "guide.jsonparser.desc":
        "Learn how to efficiently process and transform JSON data.",
    "guide.jsonparser.step1": "Enter JSON data",
    "guide.jsonparser.step2": "Validate syntax and check for errors",
    "guide.jsonparser.step3": "Convert data format",
    "guide.jsonparser.step4": "Use filtering and sorting features",
    "guide.jsonparser.step5": "Export results",
    "guide.stepsTitle": "Step-by-step Guide",
    "guide.tipsTitle": "Tips for Effective Learning",
    "guide.tipsDesc": "Useful advice for a better learning experience",
    "guide.tip1.title": "Regular Study",
    "guide.tip1.desc":
        "Studying consistently, even a little every day, is most effective.",
    "guide.tip2.title": "Practice-based Learning",
    "guide.tip2.desc": "Don't just study theory—try using the tools yourself.",
    "guide.tip3.title": "Use the Community",
    "guide.tip3.desc": "Share experiences and tips with other users.",
    "guide.tip4.title": "Collect Feedback",
    "guide.tip4.desc":
        "Let us know about any issues or suggestions you find while using the tools.",
    "guide.ctaTitle": "Get Started Now",
    "guide.ctaDesc": "Try out the tools as you follow the guide.",
    "guide.cta.minddeck": "Start MindDeck",
    "guide.cta.jsonparser": "Start JSON Parser",
    "guide.modalTitle": "Video guides in production",
    "guide.modalDesc": "Please wait a moment!",

    // Tools
    "tools.subtitle":
        "A collection of web-based tools for development and learning",
    "tools.minddeck.desc":
        "Efficient memorization tool with smart spaced repetition algorithm",
    "tools.minddeck.feature1": "Spaced repetition algorithm",
    "tools.minddeck.feature2": "Progress tracking",
    "tools.minddeck.feature3": "Dark mode support",
    "tools.minddeck.feature4": "Mobile optimized",
    "tools.jsonparser.desc":
        "Visually compare and analyze JSON data structures",
    "tools.jsonparser.feature1": "Real-time comparison",
    "tools.jsonparser.feature2": "Difference visualization",
    "tools.jsonparser.feature3": "Error validation",
    "tools.jsonparser.feature4": "Client-side processing",
    "tools.detail": "Learn More",
    "tools.useNow": "Use Now",
    "tools.coming.title": "More tools are coming soon",
    "tools.coming.desc":
        "We are developing useful tools such as AI translators and data visualization tools.",
    "tools.roadmap": "View Roadmap",

    // ToolMindDeck
    "minddeck.header.title": "MindDeck",
    "minddeck.header.desc1": "Smart flashcard-based learning tool",
    "minddeck.header.desc2":
        "Efficient memorization and spaced repetition for everyone!",
    "minddeck.features.title": "Key Features",
    "minddeck.features.1.title": "Create/Manage Flashcards",
    "minddeck.features.1.desc": "Easily add, edit, and delete cards.",
    "minddeck.features.2.title": "Smart Spaced Repetition",
    "minddeck.features.2.desc":
        "Efficient memorization with spaced repetition algorithm.",
    "minddeck.features.3.title": "Progress Tracking & Stats",
    "minddeck.features.3.desc":
        "Visually track your learning progress and achievements.",
    "minddeck.features.4.title": "Multilingual/Dark Mode Support",
    "minddeck.features.4.desc": "Learn anytime, anywhere, in any environment.",
    "minddeck.steps.title": "How to Use",
    "minddeck.steps.1":
        "Create a new deck using Add Flashcard, or load an existing deck.",
    "minddeck.steps.2": "Add cards with content to memorize to your deck.",
    "minddeck.steps.3":
        "Start study mode and review your flashcards repeatedly.",
    "minddeck.steps.4":
        "Check your progress and stats as you continue learning. You can add, delete, or edit cards within a deck.",
    "minddeck.usecases.title": "Use Cases",
    "minddeck.usecases.1":
        "Memorizing English words, Chinese characters, historical dates, etc.",
    "minddeck.usecases.2": "Programming syntax, terms, and algorithms",
    "minddeck.usecases.3": "Certification exam prep, interview prep",
    "minddeck.usecases.4":
        "Any field requiring repeated learning, such as manuals or product info",
    "minddeck.faq.title": "Frequently Asked Questions (FAQ)",
    "minddeck.faq.1.q": "Can I use it on mobile?",
    "minddeck.faq.1.a":
        "Yes, MindDeck is optimized for both mobile and desktop. On mobile Chrome, tap the three dots menu at the top right and select 'Add to Home screen' - 'Install app' to use the website like an app.",
    "minddeck.faq.2.q": "Is my learning data stored safely?",
    "minddeck.faq.2.a":
        "MindDeck does not store any learning data. All data is saved in your browser only.",
    "minddeck.faq.3.q": "Is it free to use?",
    "minddeck.faq.3.a": "Yes, MindDeck is free for everyone.",
    "minddeck.cta": "Go to MindDeck",

    // ToolJsonParser
    "json.header.title": "JSON Parser",
    "json.header.desc1": "All-in-one JSON sorting, comparison, and viewer!",
    "json.header.desc2": "Easily and quickly analyze complex JSON data.",
    "json.features.title": "Key Features",
    "json.features.1.title": "JSON Sorting",
    "json.features.1.desc": "Sort complex JSON data for easier viewing.",
    "json.features.2.title": "JSON Comparison",
    "json.features.2.desc":
        "Easily compare differences between two JSON datasets.",
    "json.features.3.title": "JSON Viewer",
    "json.features.3.desc": "Explore and analyze JSON in a tree structure.",
    "json.steps.title": "How to Use",
    "json.steps.1": "Select the desired function: sort, compare, or view.",
    "json.steps.2": "Enter your JSON data.",
    "json.steps.3": "Check the results and copy if needed.",
    "json.usecases.title": "Use Cases",
    "json.usecases.1": "Analyze API response data",
    "json.usecases.2": "Compare JSON structures by version",
    "json.usecases.3": "Visualize complex JSON trees",
    "json.usecases.4": "Data transformation and formatting",
    "json.faq.title": "Frequently Asked Questions (FAQ)",
    "json.faq.1.q": "Can it handle large JSON files?",
    "json.faq.1.a":
        "Yes, most large JSON files can be processed quickly. However, there may be limitations depending on browser memory.",
    "json.faq.2.q": "Can I save the JSON comparison results?",
    "json.faq.2.a": "Yes, you can copy the results or download them as a file.",
    "json.faq.3.q": "Can I use it on mobile?",
    "json.faq.3.a":
        "Yes, it is optimized for mobile as well. On mobile Chrome, tap the three dots menu at the top right and select 'Add to Home screen' - 'Install app' to use the website like an app.",
    "json.cta": "Go to JSON Parser",

    // Sidebar
    "sidebar.toolsOverview": "Tools Overview",
    "sidebar.companyInfo": "Company Info",

    // Terms
    "terms.title": "MewSeeds Terms of Service",
    "terms.lastUpdate": "Last update: July 3, 2025",
    "terms.section1.title": "Article 1 (Purpose)",
    "terms.section1.content":
        "This Privacy Policy sets forth the standards for the collection and use of users' personal information by the operator (hereinafter 'Operator') who operates MewSeeds and all sub-services (hereinafter 'Services') provided by MewSeeds in connection with the provision of services.",
    "terms.section2.title": "Article 2 (Service Use)",
    "terms.section2.content":
        "Users may freely use the Services. Users must comply with relevant laws and these Terms when using the Services, and must not interfere with other users' use or disrupt the normal operation of the Services. Users may not commercially use, reproduce, distribute, transmit, publicly display, or create derivative works of materials, information, or content provided by the Services without the operator's prior written consent. Users may not impersonate or use the operator's name (MewSeeds, etc.), logo, or trademarks without authorization.",
    "terms.section3.title": "Article 3 (Intellectual Property Rights)",
    "terms.section3.content":
        "All intellectual property rights related to the Services, including the name, logo, design, program, database, and UI/UX of MewSeeds, belong to the Operator. If a user violates these Terms and infringes on the operator's intellectual property rights, the operator may take civil and criminal legal action and claim damages resulting therefrom.",
    "terms.section4.title": "Article 4 (Advertisements)",
    "terms.section4.content":
        "The Operator may post advertisements on service screens, etc. Users acknowledge that advertisements may be displayed when using the Services. The operator posts advertisements for the operation and free provision of services and may generate revenue through them. The operator is not responsible for damages resulting from users' participation or transactions through advertisements. Collection and use of information necessary for providing personalized advertisements follows the Privacy Policy.",
    "terms.section5.title": "Article 5 (Disclaimer)",
    "terms.section5.content":
        "The Operator is not responsible for any damages caused by unavoidable reasons such as natural disasters, system failures, server overload, hacking, or communication failures. The operator does not guarantee the accuracy, completeness, or usefulness of information or materials obtained by users through the Services and is not responsible for damages resulting from trusting or using such information. The operator is not liable for damages for free services unless there are special provisions in relevant laws.",
    "terms.section6.title": "Article 6 (Dispute Resolution and Jurisdiction)",
    "terms.section6.content":
        "The operator and users shall sincerely consult to resolve disputes related to the use of the Services. If resolution through consultation is not possible, dispute mediation may be requested to dispute mediation agencies under the 'Content Industry Promotion Act' and 'Act on Consumer Protection in Electronic Commerce, etc.' If dispute mediation does not resolve the issue or if mediation is not desired, lawsuits shall be filed with the court having jurisdiction over the operator's address.",
    "terms.section7.title": "Article 7 (Addendum)",
    "terms.section7.content": "These Terms are effective from July 3, 2025.",

    // Privacy
    "privacy.title": "Privacy Policy",
    "privacy.lastUpdate": "Last update: July 3, 2025",
    "privacy.section1.title": "Article 1 (Purpose)",
    "privacy.section1.content":
        "This Privacy Policy sets forth the standards for the collection and use of users' personal information by the operator (hereinafter 'Operator') who operates MewSeeds and all sub-services (hereinafter 'Services') provided by MewSeeds in connection with the provision of services.",
    "privacy.section2.title":
        "Article 2 (Personal Information Items and Collection Methods)",
    "privacy.section2.content":
        "The operator may collect the following personal information for service operation and inquiry response: Email address (collected when inquired), information included in user feedback (name, contact, etc. voluntarily entered by users). The operator does not automatically collect personal information during service use, but cookies and other identification information may be collected for personalized advertising. Users can refuse cookie collection through browser settings.",
    "privacy.section3.title": "Article 3 (Purpose of Personal Information Use)",
    "privacy.section3.content":
        "The operator uses collected personal information only for the following purposes: responding to and processing user inquiries, complying with legal obligations and responding to disputes, providing personalized advertising (with consent).",
    "privacy.section4.title":
        "Article 4 (Personal Information Retention and Use Period)",
    "privacy.section4.content":
        "Collected personal information may be retained for a certain period after inquiry processing is completed, and then destroyed without delay. However, if retention is required by law, it may be kept for the period specified by the relevant law. Transaction records under the Electronic Commerce Act: 5 years, Access records under the Communications Secret Protection Act: 3 months, etc.",
    "privacy.section5.title":
        "Article 5 (Personal Information Provision to Third Parties)",
    "privacy.section5.content":
        "The operator does not provide users' personal information to third parties in principle. However, it may be exceptionally provided in the following cases: when the user has given prior consent, when provision is required by law.",
    "privacy.section6.title":
        "Article 6 (Personal Information Protection Officer)",
    "privacy.section6.content": "Name: MewSeeds, Email: mewseeds@gmail.com",
    "privacy.section7.title": "Article 7 (Rights and Exercise Methods)",
    "privacy.section7.content":
        "Users may request the operator to access, correct, delete, or suspend processing of their personal information at any time, and the operator will take prompt action in accordance with the law.",
    "privacy.section8.title": "Article 8 (Privacy Policy Changes)",
    "privacy.section8.content":
        "This policy is effective from July 3, 2025. When the operator changes this policy, it will notify changes at least 7 days before implementation, and important matters 30 days in advance.",

    // Contact
    "contact.title": "Contact",
    "contact.subtitle":
        "If you have any questions or suggestions, feel free to contact us anytime.",
    "contact.infoTitle": "Contact Information",
    "contact.emailTitle": "Email",
    "contact.emailDesc": "General inquiries or suggestions",
    "contact.siteTitle": "Website",
    "contact.siteDesc": "Main website",
    "contact.timeTitle": "Response Time",
    "contact.timeValue": "Within 24 hours",
    "contact.timeDesc": "Quick response on weekdays",
    "contact.faqTitle": "Frequently Asked Questions",
    "contact.faq1.q": "Is the service free?",
    "contact.faq1.a": "Yes, all tools are provided free of charge.",
    "contact.faq2.q": "How can I suggest a feature?",
    "contact.faq2.a":
        "Please send your suggestions by email. We will review and apply them as soon as possible.",
    "contact.faq3.q": "Where can I report bugs?",
    "contact.faq3.a":
        "If you describe the bug in detail by email, we will fix it quickly.",
};

export default en;
